import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Award, Search, Trophy, Sparkles } from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";

export default function AdminCertificates() {
  const { lang } = useLang();
  const [employees, setEmployees] = useState([]);
  const [route, setRoute] = useState([]);
  const [progressByName, setProgressByName] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      const [emps, r] = await Promise.all([
        api.get("/admin/employees"),
        api.get("/public/onboarding/route"),
      ]);
      setEmployees(emps.data || []);
      setRoute(r.data.route || []);
      setLoading(false);
    })();
  }, []);

  const totalExams = useMemo(
    () => route.reduce((a, s) => a + s.exams.length, 0),
    [route]
  );

  const loadProgress = async (name) => {
    if (progressByName[name]) return progressByName[name];
    const { data } = await api.get(
      `/public/onboarding/progress/${encodeURIComponent(name)}`
    );
    setProgressByName((m) => ({ ...m, [name]: data }));
    return data;
  };

  useEffect(() => {
    // Prefetch top employees' progress once
    employees.slice(0, 25).forEach((e) => loadProgress(e.employee_name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  const filtered = employees.filter((e) =>
    e.employee_name.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-4xl flex items-center gap-2">
            <Award className="w-8 h-8 text-canales-red" />
            {lang === "es" ? "Emitir Certificados" : "Issue Certificates"}
          </h1>
          <p className="font-body text-neutral-600 mt-1">
            {lang === "es"
              ? `Solo el admin puede otorgar el Certificado Dorado "Experto Canales". Requiere aprobar los ${totalExams} exámenes con 90%+.`
              : `Only admins can issue the "Canales Expert" Golden Certificate. Requires 90%+ on all ${totalExams} exams.`}
          </p>
        </div>
      </div>

      <div className="brutal p-4 bg-white flex items-center gap-2">
        <Search className="w-4 h-4 text-neutral-500" />
        <Input
          data-testid="admin-cert-search"
          placeholder={lang === "es" ? "Buscar empleado…" : "Search employee…"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="border-0 focus-visible:ring-0"
        />
      </div>

      {loading && <div className="font-body">…</div>}

      <div className="grid md:grid-cols-2 gap-4">
        {filtered.map((e) => {
          const p = progressByName[e.employee_name];
          const gold = p?.gold_exams ?? 0;
          const total = p?.total_exams ?? totalExams;
          const golden = p?.golden;
          return (
            <div
              key={e.employee_name}
              className={`brutal p-5 bg-white ${golden ? "ring-4 ring-canales-yellow" : ""}`}
              data-testid={`admin-cert-row-${e.employee_name}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-black text-xl truncate">
                    {e.employee_name}
                  </div>
                  <div className="text-sm font-body text-neutral-600 mt-1">
                    {lang === "es" ? "Promedio" : "Avg"} · {e.avg}% ·{" "}
                    {e.attempts} {lang === "es" ? "intentos" : "attempts"}
                  </div>
                  <div className="mt-2 text-sm font-body">
                    <span className={`font-display font-bold ${golden ? "text-canales-red" : "text-neutral-700"}`}>
                      {gold}/{total}
                    </span>{" "}
                    {lang === "es" ? "exámenes con 90%+" : "exams at 90%+"}
                  </div>
                </div>
                {golden ? (
                  <div className="text-canales-red">
                    <Sparkles className="w-8 h-8" />
                  </div>
                ) : (
                  <Trophy className="w-8 h-8 text-neutral-300" />
                )}
              </div>

              <div className="mt-3 h-2 bg-canales-cream border-2 border-black rounded-full overflow-hidden">
                <div
                  className={`h-full ${golden ? "bg-canales-yellow" : "bg-canales-blue"}`}
                  style={{ width: `${total ? (gold / total) * 100 : 0}%` }}
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {golden ? (
                  <Link
                    to={`/admin/certificate/${encodeURIComponent(e.employee_name)}`}
                    data-testid={`admin-cert-issue-${e.employee_name}`}
                    className="brutal-btn bg-canales-red text-white px-3 py-1 text-sm font-display font-bold flex items-center gap-1"
                  >
                    <Award className="w-4 h-4" /> {lang === "es" ? "Emitir certificado" : "Issue certificate"}
                  </Link>
                ) : (
                  <button
                    onClick={() =>
                      toast.info(
                        lang === "es"
                          ? `Falta ${total - gold} examen(es) con 90%+ para ${e.employee_name}`
                          : `${e.employee_name} needs ${total - gold} more exam(s) at 90%+`
                      )
                    }
                    className="brutal-btn bg-canales-cream px-3 py-1 text-sm flex items-center gap-1"
                  >
                    {lang === "es" ? "No calificado aún" : "Not yet eligible"}
                  </button>
                )}
                <Link
                  to={`/admin/employees`}
                  className="brutal-btn bg-white px-3 py-1 text-sm"
                >
                  {lang === "es" ? "Detalles" : "Details"}
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && !loading && (
        <div className="brutal p-6 bg-white text-center font-body text-neutral-600">
          {lang === "es" ? "No hay empleados aún" : "No employees yet"}
        </div>
      )}
    </motion.div>
  );
}
